import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  FiCornerUpLeft,
  FiEdit2,
  FiTrash2,
  FiHeart,
  FiMoreHorizontal,
  FiChevronDown,
  FiChevronRight,
  FiMessageCircle,
  FiAtSign,
} from "react-icons/fi";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { showErrorToast as ToastError, showSuccessToast as ToastSuccess } from "../../../utils/Toaster/AppToast";
import { fixGrammarAndSpell } from "../../../services/ai.service";
import {
  getTaskComments,
  createTaskComment,
  updateTaskComment,
  deleteTaskComment,
  reactToTaskComment,
} from "../../../services/taskComment.service";
import { getTokenbyData, getUser } from "../../../services/user.service";
import ConfirmDialog from "../../../sharedComponents/ConfirmDialog/ConfirmDialog";

dayjs.extend(relativeTime);

const C = {
  bg: "#FFFFFF",
  surface: "#FAFBFC",
  border: "#E5E7EB",
  borderHover: "#D1D5DB",
  text: "#111827",
  textSub: "#6B7280",
  textMuted: "#9CA3AF",
  accent: "#0D9488",
  accentSoft: "#F0FDFA",
  thread: "#E5E7EB",
  hover: "#F9FAFB",
  danger: "#DC2626",
};

const formatTimestamp = (iso) => {
  if (!iso) return "";
  const d = dayjs(iso);
  const diffH = dayjs().diff(d, "hour");
  if (diffH < 24) return d.fromNow();
  return d.format("DD MMM YYYY, hh:mm A");
};

const Initials = ({ name }) =>
  ((name || "?")[0] || "?").toUpperCase();

// ─── Mention text renderer ────────────────────────────────────────────────────
// Handles store spaces as underscores (@VEERAMANI_VIDHUN) — display replaces them back
const renderMentionText = (text) => {
  if (!text) return null;
  const parts = text.split(/(@\w+)/g);
  return parts.map((part, i) =>
    part.startsWith("@") ? (
      <Box
        key={i}
        component="span"
        sx={{
          color: C.accent,
          fontWeight: 700,
          bgcolor: `${C.accent}14`,
          borderRadius: "4px",
          px: "4px",
          py: "1px",
          fontSize: "inherit",
        }}
      >
        {part.replace(/_/g, " ")}
      </Box>
    ) : (
      part
    )
  );
};

// ─── Comment Composer ─────────────────────────────────────────────────────────
const CommentComposer = React.memo(function CommentComposer({
  value,
  onChange,
  onSubmit,
  onCancel,
  submitLabel = "Comment",
  placeholder = "Write a comment…",
  autoFocus = false,
  size = "md",
  disabled = false,
  users = [],
  onInsertMention, // called with user._id when a mention is selected
}) {
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [fixed, setFixed] = useState(false);

  const handleFix = async () => {
    if (isFixing || !value?.trim()) return;
    setIsFixing(true);
    try {
      const res = await fixGrammarAndSpell(value.trim());
      const corrected = res?.correctedText;
      if (corrected) {
        onChange(corrected);
        setFixed(true);
        setTimeout(() => setFixed(false), 2000);
      }
    } catch (_) {
      // silently ignore
    } finally {
      setIsFixing(false);
    }
  };

  // @mention state
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionHighlight, setMentionHighlight] = useState(0);
  const [dropdownPos, setDropdownPos] = useState(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  // Smart positioning: open above if enough space, otherwise open below
  useEffect(() => {
    if (mentionOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceAbove = rect.top - 8;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const maxH = Math.min(300, Math.max(spaceAbove, spaceBelow));

      if (spaceAbove >= 150 || spaceAbove >= spaceBelow) {
        setDropdownPos({
          left: rect.left,
          width: rect.width,
          bottom: window.innerHeight - rect.top + 6,
          top: undefined,
          maxHeight: Math.min(300, spaceAbove),
        });
      } else {
        setDropdownPos({
          left: rect.left,
          width: rect.width,
          top: rect.bottom + 6,
          bottom: undefined,
          maxHeight: Math.min(300, spaceBelow),
        });
      }
    }
  }, [mentionOpen]);

  const filteredUsers = useMemo(() => {
    if (!mentionOpen) return [];
    const q = mentionQuery.toLowerCase();
    return users
      .filter((u) => {
        const n = (u.name || u.userName || "").toLowerCase();
        const un = (u.userName || "").toLowerCase();
        return !q || n.includes(q) || un.includes(q);
      })
      .slice(0, 8);
  }, [mentionOpen, mentionQuery, users]);

  const handleChange = (newVal) => {
    onChange(newVal);
    const el = inputRef.current;
    if (!el) return;
    const cursor = el.selectionStart;
    const before = newVal.slice(0, cursor);
    // Only trigger when @ is at the start OR preceded by a space/newline
    const m = before.match(/(^|\s)@(\w*)$/);
    if (m) {
      setMentionQuery(m[2]);
      setMentionOpen(true);
      setMentionHighlight(0);
    } else {
      setMentionOpen(false);
    }
  };

  const insertMention = useCallback(
    (user) => {
      const el = inputRef.current;
      if (!el) return;
      const cursor = el.selectionStart;
      const before = value.slice(0, cursor);
      const after = value.slice(cursor);
      const atPos = before.lastIndexOf("@");
      // Keep only word characters so the handle stays within @(\w+) regex range
      const handle = (user.userName || user.name || "user")
        .trim()
        .replace(/[^\w\s]/g, "")   // strip (, ), ., @ and other special chars
        .replace(/\s+/g, "_")      // spaces → underscores
        .replace(/_+/g, "_")       // collapse consecutive underscores
        .replace(/^_|_$/g, "");    // trim leading/trailing underscores
      const newText = before.slice(0, atPos) + `@${handle} ` + after;
      onChange(newText);
      setMentionOpen(false);
      // Notify parent with the selected user's _id
      if (user._id) onInsertMention?.(user._id);
      requestAnimationFrame(() => {
        const pos = atPos + handle.length + 2;
        el.focus();
        el.setSelectionRange(pos, pos);
      });
    },
    [value, onChange, onInsertMention]
  );

  const handleKey = (e) => {
    if (mentionOpen && filteredUsers.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionHighlight((h) => Math.min(h + 1, filteredUsers.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionHighlight((h) => Math.max(h - 1, 0));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(filteredUsers[mentionHighlight]);
        return;
      }
      if (e.key === "Escape") {
        setMentionOpen(false);
        return;
      }
    }
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      onSubmit?.();
    }
    if (e.key === "Escape" && !mentionOpen) onCancel?.();
  };

  // Close on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setMentionOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  return (
    <Box ref={containerRef} sx={{ position: "relative" }}>
      <Box
        sx={{
          border: `1px solid ${focused ? C.accent : C.border}`,
          borderRadius: "8px",
          background: C.bg,
          transition: "border-color 0.15s, box-shadow 0.15s",
          boxShadow: focused ? `0 0 0 3px ${C.accent}1A` : "none",
          overflow: "hidden",
        }}
      >
        <TextField
          inputRef={inputRef}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          multiline
          minRows={size === "sm" ? 1 : 2}
          maxRows={8}
          fullWidth
          variant="standard"
          inputProps={{ style: { padding: "8px 12px" } }}
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: "13.5px",
              lineHeight: 1.55,
              color: C.text,
              "& textarea::placeholder": { color: C.textMuted, opacity: 1 },
            },
          }}
        />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1.5, py: 0.8, borderTop: `1px solid ${C.border}`, background: C.surface }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: "11px", color: C.textMuted }}>
              <kbd style={{ padding: "1px 5px", fontSize: "10px", fontFamily: "inherit", border: `1px solid ${C.border}`, borderRadius: "4px", background: C.bg, color: C.textSub }}>Ctrl</kbd>
              {" + "}
              <kbd style={{ padding: "1px 5px", fontSize: "10px", fontFamily: "inherit", border: `1px solid ${C.border}`, borderRadius: "4px", background: C.bg, color: C.textSub }}>Enter</kbd>
              {" to submit"}
            </Typography>
            <Box
              sx={{
                display: "inline-flex", alignItems: "center", gap: "3px",
                px: "6px", py: "2px", borderRadius: "4px",
                bgcolor: C.accentSoft, border: `1px solid ${C.accent}22`,
              }}
            >
              <FiAtSign size={9} color={C.accent} />
              <Typography sx={{ fontSize: "10px", color: C.accent, fontWeight: 600 }}>mention</Typography>
            </Box>
            <Tooltip title="Fix spelling & grammar (AI)">
              <Button
                size="small"
                onClick={handleFix}
                disabled={isFixing || !value?.trim()}
                startIcon={
                  isFixing
                    ? <CircularProgress size={11} sx={{ color: "#7C3AED" }} />
                    : <AutoFixHighIcon sx={{ fontSize: "13px !important" }} />
                }
                sx={{
                  textTransform: "none",
                  fontSize: "11px",
                  fontWeight: 600,
                  px: 1,
                  py: 0.2,
                  minHeight: 0,
                  borderRadius: "5px",
                  color: fixed ? "#16A34A" : "#7C3AED",
                  bgcolor: fixed ? "#F0FDF4" : "#FAF5FF",
                  border: `1px solid ${fixed ? "#86EFAC" : "#DDD6FE"}`,
                  transition: "all 0.2s",
                  "&:hover": { bgcolor: "#EDE9FE", color: "#6D28D9", border: "1px solid #C4B5FD" },
                  "&.Mui-disabled": { opacity: 0.45 },
                }}
              >
                {isFixing ? "Fixing…" : fixed ? "Fixed!" : "Fix"}
              </Button>
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {onCancel && (
              <Button
                size="small"
                onClick={onCancel}
                sx={{ textTransform: "none", color: C.textSub, fontWeight: 600, fontSize: "12px", px: 1.2, py: 0.4, minHeight: 0, "&:hover": { background: C.hover, color: C.text } }}
              >
                Cancel
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              disabled={disabled || !value?.trim()}
              onClick={onSubmit}
              sx={{ textTransform: "none", background: C.accent, fontWeight: 600, fontSize: "12px", px: 1.6, py: 0.4, minHeight: 0, boxShadow: "none", "&:hover": { background: "#0F766E", boxShadow: "none" }, "&.Mui-disabled": { background: "#E5E7EB", color: "#9CA3AF" } }}
            >
              {submitLabel}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* @mention dropdown — position:fixed so it escapes overflow:hidden dialogs */}
      {mentionOpen && filteredUsers.length > 0 && dropdownPos && (
        <Paper
          elevation={0}
          sx={{
            position: "fixed",
            left: dropdownPos.left,
            width: Math.min(dropdownPos.width, 280),
            ...(dropdownPos.bottom !== undefined
              ? { bottom: dropdownPos.bottom }
              : { top: dropdownPos.top }),
            zIndex: 9999,
            borderRadius: "12px",
            border: `1.5px solid ${C.border}`,
            boxShadow: "0 12px 36px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)",
            overflow: "hidden",
            bgcolor: "#fff",
            maxHeight: dropdownPos.maxHeight || 300,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box sx={{ px: 1.5, py: 0.7, bgcolor: C.accentSoft, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 0.6 }}>
            <FiAtSign size={10} color={C.accent} />
            <Typography sx={{ fontSize: "10px", fontWeight: 800, color: C.accent, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Mention
            </Typography>
            {mentionQuery && (
              <Typography sx={{ fontSize: "10px", color: C.textMuted, ml: 0.5 }}>
                "{mentionQuery}"
              </Typography>
            )}
          </Box>

          {/* User list */}
          <Box sx={{ py: 0.5, flex: 1, overflowY: "auto", minHeight: 0 }}>
            {filteredUsers.map((user, i) => {
              const name = user.name || user.userName || "Unknown";
              const handle = user.userName || "";
              const isActive = i === mentionHighlight;
              return (
                <Box
                  key={user._id || user.id || i}
                  onMouseDown={(e) => { e.preventDefault(); insertMention(user); }}
                  onMouseEnter={() => setMentionHighlight(i)}
                  sx={{
                    px: 1.2, py: 0.7, mx: 0.5, borderRadius: "8px",
                    display: "flex", alignItems: "center", gap: 1,
                    cursor: "pointer",
                    bgcolor: isActive ? C.accentSoft : "transparent",
                    transition: "background 0.1s",
                  }}
                >
                  {/* Avatar circle */}
                  <Box sx={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    bgcolor: isActive ? C.accent : "#E2E8F0",
                    color: isActive ? "#fff" : C.textSub,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "11px", fontWeight: 700,
                    transition: "all 0.1s",
                  }}>
                    {name[0].toUpperCase()}
                  </Box>

                  {/* Name + handle */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: C.text, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {name}
                    </Typography>
                    {handle && (
                      <Typography sx={{ fontSize: "10.5px", color: C.textMuted, lineHeight: 1.2 }}>
                        @{handle.toLowerCase()}
                      </Typography>
                    )}
                  </Box>

                  {/* Enter hint */}
                  {isActive && (
                    <Box sx={{ flexShrink: 0, px: 0.6, py: 0.2, borderRadius: "4px", bgcolor: `${C.accent}18`, display: "flex", alignItems: "center" }}>
                      <Typography sx={{ fontSize: "10px", color: C.accent, fontWeight: 700, lineHeight: 1 }}>↵</Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Footer hint */}
          <Box sx={{ px: 1.5, py: 0.6, borderTop: `1px solid ${C.border}`, bgcolor: "#FAFAFA", display: "flex", gap: 1.5 }}>
            <Typography sx={{ fontSize: "10px", color: C.textMuted }}>↑↓ navigate</Typography>
            <Typography sx={{ fontSize: "10px", color: C.textMuted }}>↵ select</Typography>
            <Typography sx={{ fontSize: "10px", color: C.textMuted }}>Esc close</Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
});

const ReactionButton = ({ active, count, onClick }) => (
  <Tooltip title={active ? "Remove reaction" : "Like"}>
    <Button
      size="small"
      onClick={onClick}
      sx={{
        minWidth: 0, px: 1, py: 0.2, borderRadius: "6px", textTransform: "none",
        color: active ? C.danger : C.textSub, fontSize: "11.5px", fontWeight: 600,
        "&:hover": { background: C.hover },
      }}
      startIcon={<FiHeart size={12} fill={active ? C.danger : "none"} />}
    >
      {count > 0 ? count : "Like"}
    </Button>
  </Tooltip>
);

const MAX_REPLY_DEPTH = 3;

const CommentNode = ({
  node,
  currentUserId,
  isAdmin,
  onReply,
  onEdit,
  onDelete,
  onReact,
  depth = 0,
  forceExpanded = false,
  users = [],
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const isAuthor = String(node.author) === String(currentUserId);
  const canEdit = (isAuthor || isAdmin) && !node.isDeleted;
  const canDelete = isAuthor && !node.isDeleted;

  useEffect(() => {
    if (forceExpanded) setCollapsed(false);
  }, [forceExpanded]);

  const myReaction = node.reactions?.find((r) => String(r.user) === String(currentUserId));
  const hasReplies = node.replies?.length > 0;
  const indent = depth === 0 ? 0 : 28;

  return (
    <Box sx={{ position: "relative", pl: `${indent}px`, mt: depth === 0 ? 0 : 1.5 }}>
      {depth > 0 && (
        <Box sx={{ position: "absolute", left: 12, top: 0, bottom: 0, width: "2px", background: C.thread, borderRadius: 1 }} />
      )}

      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar
          src={node.authorAvatar || undefined}
          sx={{
            width: depth === 0 ? 34 : 28, height: depth === 0 ? 34 : 28,
            bgcolor: C.accent, fontSize: depth === 0 ? "13px" : "11px",
            fontWeight: 700, flexShrink: 0, mt: 0.2,
          }}
        >
          <Initials name={node.authorName} />
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.8}>
            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: C.text }}>
              {node.authorName || "Unknown user"}
            </Typography>
            {node.authorRole && (
              <Typography sx={{ fontSize: "11px", color: C.textMuted }}>· {node.authorRole}</Typography>
            )}
            <Tooltip title={dayjs(node.createdAt).format("DD MMM YYYY, hh:mm A")}>
              <Typography sx={{ fontSize: "11.5px", color: C.textMuted }}>
                · {formatTimestamp(node.createdAt)}
              </Typography>
            </Tooltip>
            {node.edited && (
              <Typography sx={{ fontSize: "11px", color: C.textMuted, fontStyle: "italic" }}>· edited</Typography>
            )}
            <Box sx={{ flex: 1 }} />
            {(canEdit || canDelete) && (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  sx={{ color: C.textMuted, "&:hover": { color: C.text } }}
                >
                  <FiMoreHorizontal size={14} />
                </IconButton>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                  PaperProps={{ sx: { borderRadius: "8px", minWidth: 140 } }}
                >
                  {canEdit && (
                    <MenuItem onClick={() => { setMenuAnchor(null); onEdit(node); }} sx={{ fontSize: "13px", gap: 1 }}>
                      <FiEdit2 size={13} /> Edit
                    </MenuItem>
                  )}
                  {canDelete && (
                    <MenuItem onClick={() => { setMenuAnchor(null); onDelete(node); }} sx={{ fontSize: "13px", gap: 1, color: C.danger }}>
                      <FiTrash2 size={13} /> Delete
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
          </Stack>

          {/* Body — render @mentions highlighted */}
          <Typography
            component="div"
            sx={{
              fontSize: "13.5px",
              color: node.isDeleted ? C.textMuted : C.text,
              lineHeight: 1.55,
              mt: 0.3,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontStyle: node.isDeleted ? "italic" : "normal",
            }}
          >
            {node.isDeleted ? "[deleted]" : renderMentionText(node.text)}
          </Typography>

          {/* Actions row */}
          {!node.isDeleted && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.6, ml: -1 }}>
              <ReactionButton
                active={Boolean(myReaction)}
                count={node.reactionCount || node.reactions?.length || 0}
                onClick={() => onReact(node)}
              />
              {depth < MAX_REPLY_DEPTH && (
                <Button
                  size="small"
                  startIcon={<FiCornerUpLeft size={12} />}
                  onClick={() => onReply(node)}
                  sx={{ textTransform: "none", color: C.textSub, fontWeight: 600, fontSize: "11.5px", px: 1, py: 0.2, borderRadius: "6px", "&:hover": { background: C.hover, color: C.text } }}
                >
                  Reply
                </Button>
              )}
              {hasReplies && (
                <Button
                  size="small"
                  startIcon={collapsed ? <FiChevronRight size={12} /> : <FiChevronDown size={12} />}
                  onClick={() => setCollapsed((v) => !v)}
                  sx={{ textTransform: "none", color: C.textSub, fontWeight: 600, fontSize: "11.5px", px: 1, py: 0.2, borderRadius: "6px", "&:hover": { background: C.hover, color: C.text } }}
                >
                  {collapsed ? `Show ${node.replies.length} ${node.replies.length === 1 ? "reply" : "replies"}` : "Hide replies"}
                </Button>
              )}
            </Stack>
          )}
        </Box>
      </Stack>

      {/* Children */}
      {hasReplies && !collapsed && (
        <Box>
          {node.replies.map((child) => (
            <CommentNode
              key={child._id}
              node={child}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              onReact={onReact}
              depth={depth + 1}
              forceExpanded={!collapsed}
              users={users}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const Comments = ({
  taskId,
  currentUserId: propUserId,
  currentUserName: propUserName,
  isAdmin: propIsAdmin,
  currentUserAvatar: propAvatar,
}) => {
  const [me, setMe] = useState({
    userId: propUserId || null,
    name: propUserName || "",
    avatar: propAvatar || "",
    isAdmin: Boolean(propIsAdmin),
  });

  useEffect(() => {
    let cancelled = false;
    if (propUserId && propUserName !== undefined) return;
    (async () => {
      try {
        const res = await getTokenbyData();
        const d = res?.data || {};
        if (!cancelled) {
          setMe((prev) => ({
            userId: prev.userId || d.userId || null,
            name: prev.name || d.name || "",
            avatar: prev.avatar || d.file?.location || d.profilePic || "",
            isAdmin: prev.isAdmin || Boolean(d.isOverAllAdmin),
          }));
        }
      } catch (e) { }
    })();
    return () => { cancelled = true; };
  }, [propUserId, propUserName]);

  const currentUserId = me.userId;
  const currentUserName = me.name;
  const currentUserAvatar = me.avatar;
  const isAdmin = me.isAdmin;

  // Users for @mention
  const [mentionUsers, setMentionUsers] = useState([]);
  useEffect(() => {
    getUser()
      .then((res) => {
        if (res?.error === false) setMentionUsers(res.data || []);
      })
      .catch(() => {});
  }, []);

  // Track mentioned _ids per composer (insertion-order, deduped)
  const [draftMentions, setDraftMentions] = useState([]);
  const [replyMentions, setReplyMentions] = useState([]);
  const [editMentions, setEditMentions] = useState([]);

  const addMention = (setter) => (id) =>
    setter((prev) => (prev.includes(id) ? prev : [...prev, id]));

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [pagination, setPagination] = useState({ total: 0 });

  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);

  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const mutateTree = useCallback((tree, predicate, mutator) => {
    return tree.map((node) => {
      const next = predicate(node) ? mutator(node) : node;
      if (next.replies?.length) return { ...next, replies: mutateTree(next.replies, predicate, mutator) };
      return next;
    });
  }, []);

  const insertReply = useCallback((tree, parentId, newNode) => {
    return tree.map((node) => {
      if (String(node._id) === String(parentId)) {
        return { ...node, replies: [...(node.replies || []), { ...newNode, replies: [] }], replyCount: (node.replyCount || 0) + 1 };
      }
      if (node.replies?.length) return { ...node, replies: insertReply(node.replies, parentId, newNode) };
      return node;
    });
  }, []);

  const loadComments = useCallback(async (pageNum = 1, append = false) => {
    if (!taskId) return;
    setLoading(true);
    try {
      const res = await getTaskComments(taskId, { page: pageNum, limit: 20 });
      if (res?.error === false) {
        setComments((prev) => (append ? [...prev, ...res.data] : res.data));
        setPagination(res.pagination || {});
        setHasMore(res.pagination?.hasMore || false);
        setPage(pageNum);
      }
    } catch (err) {
      ToastError("Failed to load comments");
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { loadComments(1, false); }, [loadComments]);

  const handlePostRoot = async () => {
    if (!draft.trim()) return;
    setPosting(true);
    try {
      const res = await createTaskComment(taskId, { text: draft.trim(), mentions: draftMentions });
      if (res?.error === false) {
        setComments((prev) => [{ ...res.data, replies: [] }, ...prev]);
        setDraft("");
        setDraftMentions([]);
        ToastSuccess("Comment posted");
      }
    } catch (e) {
      ToastError(e?.data?.message || "Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  const handlePostReply = async () => {
    if (!replyTo || !replyText.trim()) return;
    try {
      const res = await createTaskComment(taskId, {
        parent: replyTo._id,
        text: replyText.trim(),
        mentions: replyMentions,
      });
      if (res?.error === false) {
        setComments((prev) => insertReply(prev, replyTo._id, res.data));
        setReplyTo(null);
        setReplyText("");
        setReplyMentions([]);
      }
    } catch (e) {
      ToastError(e?.data?.message || "Failed to reply");
    }
  };

  const handleSaveEdit = async () => {
    if (!editing || !editText.trim()) return;
    try {
      // Derive final mentions from the actual text so additions, deletions,
      // and manual edits are all reflected — not just dropdown selections.
      const handles = (editText.match(/@(\w+)/g) || []).map((h) => h.slice(1).toLowerCase());
      const textMentions = mentionUsers
        .filter((u) => {
          const handle = (u.userName || u.name || "")
            .replace(/[^\w\s]/g, "")
            .replace(/\s+/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_|_$/g, "")
            .toLowerCase();
          return handles.some((h) => h === handle);
        })
        .map((u) => u._id);
      const res = await updateTaskComment(editing._id, { text: editText.trim(), mentions: textMentions });
      if (res?.error === false) {
        setComments((prev) =>
          mutateTree(prev, (n) => String(n._id) === String(editing._id), (n) => ({ ...n, ...res.data }))
        );
        setEditing(null);
        setEditText("");
        setEditMentions([]);
      }
    } catch (e) {
      ToastError(e?.data?.message || "Failed to update");
    }
  };

  const handleDelete = (node) => setDeleteTarget(node);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteTaskComment(deleteTarget._id);
      if (res?.error === false) {
        setComments((prev) =>
          mutateTree(prev, (n) => String(n._id) === String(deleteTarget._id), (n) => ({ ...n, isDeleted: true, text: "" }))
        );
        setDeleteTarget(null);
      }
    } catch (e) {
      ToastError(e?.data?.message || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const handleReact = async (node) => {
    const hadReaction = node.reactions?.some((r) => String(r.user) === String(currentUserId));
    setComments((prev) =>
      mutateTree(prev, (n) => String(n._id) === String(node._id), (n) => ({
        ...n,
        reactions: hadReaction
          ? n.reactions.filter((r) => String(r.user) !== String(currentUserId))
          : [...(n.reactions || []), { user: currentUserId, type: "like" }],
        reactionCount: (n.reactionCount || n.reactions?.length || 0) + (hadReaction ? -1 : 1),
      }))
    );
    try {
      await reactToTaskComment(node._id, "like");
    } catch (e) {
      setComments((prev) =>
        mutateTree(prev, (n) => String(n._id) === String(node._id), (n) => ({
          ...n,
          reactions: hadReaction
            ? [...(n.reactions || []), { user: currentUserId, type: "like" }]
            : n.reactions.filter((r) => String(r.user) !== String(currentUserId)),
        }))
      );
    }
  };

  const startEdit = (node) => {
    setEditing(node);
    setEditText(node.text);
    setEditMentions(node.mentions || []);
  };

  const totalCount = useMemo(() => {
    const walk = (nodes) => nodes.reduce((acc, n) => acc + 1 + walk(n.replies || []), 0);
    return walk(comments);
  }, [comments]);

  return (
    <Box>
      {/* Root composer */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2.5 }}>
        <Box sx={{ flex: 1 }}>
          <CommentComposer
            value={draft}
            onChange={setDraft}
            onSubmit={handlePostRoot}
            submitLabel="Comment"
            placeholder="Add a comment… (type @ to mention someone)"
            disabled={posting}
            users={mentionUsers}
            onInsertMention={addMention(setDraftMentions)}
          />
        </Box>
      </Stack>

      {/* List */}
      {loading && comments.length === 0 ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={20} sx={{ color: C.accent }} />
        </Stack>
      ) : comments.length === 0 ? (
        <Stack alignItems="center" spacing={1.2} sx={{ py: 5 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: "50%", background: C.accentSoft, display: "flex", alignItems: "center", justifyContent: "center", color: C.accent }}>
            <FiMessageCircle size={20} />
          </Box>
          <Typography sx={{ fontSize: "13.5px", fontWeight: 600, color: C.text }}>No comments yet</Typography>
          <Typography sx={{ fontSize: "12px", color: C.textMuted }}>Be the first to share your thoughts.</Typography>
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          {comments.map((node) => (
            <Box key={node._id}>
              <CommentNode
                node={node}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                onReply={(n) => { setReplyTo(n); setReplyText(""); }}
                onEdit={startEdit}
                onDelete={handleDelete}
                onReact={handleReact}
                users={mentionUsers}
              />

              {/* Inline reply composer */}
              {replyTo && String(replyTo._id) === String(node._id) && (
                <Box sx={{ ml: "50px", mt: 1.2 }}>
                  <CommentComposer
                    value={replyText}
                    onChange={setReplyText}
                    onSubmit={handlePostReply}
                    onCancel={() => { setReplyTo(null); setReplyText(""); setReplyMentions([]); }}
                    submitLabel="Reply"
                    placeholder={`Reply to ${replyTo.authorName || "comment"}… (type @ to mention)`}
                    autoFocus
                    size="sm"
                    users={mentionUsers}
                    onInsertMention={addMention(setReplyMentions)}
                  />
                </Box>
              )}

              {/* Inline edit composer */}
              {editing && String(editing._id) === String(node._id) && (
                <Box sx={{ ml: "50px", mt: 1.2 }}>
                  <CommentComposer
                    value={editText}
                    onChange={setEditText}
                    onSubmit={handleSaveEdit}
                    onCancel={() => { setEditing(null); setEditText(""); setEditMentions([]); }}
                    submitLabel="Save"
                    autoFocus
                    size="sm"
                    users={mentionUsers}
                    onInsertMention={addMention(setEditMentions)}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {/* Floating reply (nested) */}
      {replyTo && !comments.find((c) => String(c._id) === String(replyTo._id)) && (
        <Box sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1300, width: 380, maxWidth: "90vw", background: C.bg, border: `1px solid ${C.border}`, borderRadius: "10px", boxShadow: "0 12px 32px rgba(15,23,42,0.18)", p: 2 }}>
          <Typography sx={{ fontSize: "12px", color: C.textMuted, mb: 1 }}>
            Replying to <strong style={{ color: C.text }}>{replyTo.authorName}</strong>
          </Typography>
          <CommentComposer
            value={replyText}
            onChange={setReplyText}
            onSubmit={handlePostReply}
            onCancel={() => { setReplyTo(null); setReplyText(""); setReplyMentions([]); }}
            submitLabel="Reply"
            autoFocus
            size="sm"
            users={mentionUsers}
            onInsertMention={addMention(setReplyMentions)}
          />
        </Box>
      )}

      {/* Floating edit (nested) */}
      {editing && !comments.find((c) => String(c._id) === String(editing._id)) && (
        <Box sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1300, width: 380, maxWidth: "90vw", background: C.bg, border: `1px solid ${C.border}`, borderRadius: "10px", boxShadow: "0 12px 32px rgba(15,23,42,0.18)", p: 2 }}>
          <Typography sx={{ fontSize: "12px", color: C.textMuted, mb: 1 }}>Editing your comment</Typography>
          <CommentComposer
            value={editText}
            onChange={setEditText}
            onSubmit={handleSaveEdit}
            onCancel={() => { setEditing(null); setEditText(""); setEditMentions([]); }}
            submitLabel="Save"
            autoFocus
            size="sm"
            users={mentionUsers}
            onInsertMention={addMention(setEditMentions)}
          />
        </Box>
      )}

      {/* Load more */}
      {hasMore && comments.length > 0 && (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Button
            onClick={() => loadComments(page + 1, true)}
            disabled={loading}
            sx={{ textTransform: "none", color: C.accent, fontWeight: 600, fontSize: "12.5px", "&:hover": { background: C.accentSoft } }}
          >
            {loading ? "Loading…" : `Load more (${pagination.total - comments.length} remaining)`}
          </Button>
        </Stack>
      )}

      {comments.length > 0 && (
        <Typography sx={{ mt: 2, fontSize: "11.5px", color: C.textMuted, textAlign: "center" }}>
          Showing {totalCount} {totalCount === 1 ? "comment" : "comments"}
        </Typography>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
        onConfirm={confirmDelete}
        loading={deleting}
        variant="danger"
        title="Delete this comment?"
        description={deleteTarget?.replies?.length ? "The comment text will be removed but its replies will remain visible in the thread." : "This action cannot be undone."}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </Box>
  );
};

export default Comments;
